import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Check, FileText, Coins, AlertOctagon, Activity } from 'lucide-react';
import { useState } from 'react';
import { format } from 'date-fns';
import type { UserDetail } from '../types';

export function OverviewPanel({ detail }: { detail: UserDetail }) {
  const [copied, setCopied] = useState(false);
  const copyId = async () => {
    await navigator.clipboard.writeText(detail.user.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const sub = detail.subscription;
  const stats = detail.stats;

  return (
    <div className="space-y-5">
      {/* Identity card */}
      <div className="rounded-xl border border-border/60 bg-card p-5">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">
          Identity
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Email" value={detail.user.email} />
          <Field
            label="User ID"
            value={
              <div className="flex items-center gap-2">
                <code className="text-xs bg-muted/60 px-1.5 py-0.5 rounded truncate">
                  {detail.user.id}
                </code>
                <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={copyId}>
                  {copied ? (
                    <Check className="h-3 w-3 text-success" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            }
          />
          <Field
            label="Registered"
            value={format(new Date(detail.user.created_at), 'dd.MM.yyyy HH:mm')}
          />
          <Field
            label="Last sign-in"
            value={
              detail.user.last_sign_in_at
                ? format(new Date(detail.user.last_sign_in_at), 'dd.MM.yyyy HH:mm')
                : '—'
            }
          />
          <Field
            label="Last activity"
            value={
              stats.last_activity_at
                ? format(new Date(stats.last_activity_at), 'dd.MM.yyyy HH:mm')
                : 'Never'
            }
          />
          <Field
            label="Roles"
            value={
              <div className="flex gap-1 flex-wrap">
                {detail.is_admin && (
                  <Badge className="bg-primary/15 text-primary border border-primary/30 hover:bg-primary/20">
                    Admin
                  </Badge>
                )}
                {detail.is_banned && <Badge variant="destructive">Banned</Badge>}
                {!detail.is_admin && !detail.is_banned && (
                  <Badge variant="outline">User</Badge>
                )}
              </div>
            }
          />
        </div>
      </div>

      {/* Subscription card */}
      <div className="rounded-xl border border-border/60 bg-card p-5">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">
          Subscription
        </h4>
        {sub ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
            <Field
              label="Tier"
              value={
                <Badge variant="outline" className="capitalize">
                  {sub.tier}
                </Badge>
              }
            />
            <Field
              label="Status"
              value={
                <Badge
                  variant={sub.status === 'active' ? 'default' : 'secondary'}
                  className="capitalize"
                >
                  {sub.status}
                </Badge>
              }
            />
            <Field label="Cycle" value={sub.billing_cycle} />
            <Field
              label="Credits"
              value={`${sub.credits_remaining} / ${sub.credits_monthly}`}
            />
            <Field
              label="Period end"
              value={
                sub.current_period_end
                  ? format(new Date(sub.current_period_end), 'dd.MM.yyyy')
                  : '—'
              }
            />
            <Field label="Cancel at end" value={sub.cancel_at_period_end ? 'Yes' : 'No'} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No subscription on record.</p>
        )}
      </div>

      {/* Activity stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={FileText} label="Extractions total" value={stats.extractions_total} />
        <Stat icon={Activity} label="Last 30 days" value={stats.extractions_30d} />
        <Stat icon={Coins} label="Top-ups" value={stats.topups_count} />
        <Stat
          icon={AlertOctagon}
          label="Errors"
          value={stats.graph_errors_count + stats.error_reports_count}
          tone={stats.graph_errors_count + stats.error_reports_count > 0 ? 'warning' : 'default'}
        />
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">
        {label}
      </div>
      <div className="text-sm break-words">{value}</div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  tone = 'default',
}: {
  icon: typeof FileText;
  label: string;
  value: number;
  tone?: 'default' | 'warning';
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-4">
      <div className="flex items-center justify-between mb-1.5">
        <Icon
          className={`h-4 w-4 ${tone === 'warning' ? 'text-warning' : 'text-muted-foreground'}`}
        />
      </div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
